import { messages, type Lang, type Messages } from "../i18n/index";
import type { Store, ViewState } from "./store";

/** Runs `apply` now and again after each language change. */
export function localize(store: Store, apply: (m: Messages, lang: Lang) => void): void {
  let last: Lang | null = null;
  const run = (s: ViewState): void => {
    if (s.lang === last) return;
    last = s.lang;
    apply(messages(s.lang), s.lang);
  };
  store.subscribe(run);
  run(store.state);
}

export function button(label: string, onClick: () => void, className?: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = label;
  if (className) b.className = className;
  b.addEventListener("click", onClick);
  return b;
}

/** Drops focus after a mouse or touch click so the window shortcuts, which skip buttons, keep working. */
export function blurOnPointerClick(el: HTMLElement): void {
  el.addEventListener("click", (e) => {
    if (e.detail > 0) el.blur();
  });
}

/** Writes text only when it changed, so per-frame refreshes leave the DOM alone. */
export function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}

// drawn as SVG because the ⏸/▶ text glyphs render at very different sizes across system fonts
const ICON_PATHS = {
  Pause: "M6 4h4v16H6zM14 4h4v16h-4z",
  Play: "M7 4l13 8-13 8z",
};

function icon(name: keyof typeof ICON_PATHS): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", ICON_PATHS[name]);
  svg.append(path);
  return svg;
}

/** Round play/pause button; its accessible name is "Pause" while running and "Play" while paused. */
export function playButton(
  onClick: () => void,
  store: Store,
): {
  el: HTMLButtonElement;
  sync(paused: boolean): void;
} {
  const el = button("", onClick, "play");
  let shown: "Play" | "Pause" | null = null;
  return {
    el,
    sync(paused) {
      const kind = paused ? "Play" : "Pause";
      const { player } = messages(store.state.lang);
      const name = paused ? player.play : player.pause;
      if (el.getAttribute("aria-label") !== name) el.setAttribute("aria-label", name);
      if (shown === kind) return;
      shown = kind;
      el.replaceChildren(icon(kind));
    },
  };
}

/** A labelled switch with no state of its own; the caller keeps `input.checked` right. */
export function switchRow(
  label: (m: Messages) => string,
  store: Store,
): { row: HTMLLabelElement; input: HTMLInputElement } {
  const row = document.createElement("label");
  row.className = "switch-row";
  const text = document.createElement("span");
  localize(store, (m) => setText(text, label(m)));
  const input = document.createElement("input");
  input.type = "checkbox";
  input.className = "switch";
  input.setAttribute("role", "switch");
  row.append(text, input);
  return { row, input };
}

/** A labelled switch kept in sync with one boolean view of the store. */
export function storeSwitch(
  label: (m: Messages) => string,
  store: Store,
  read: (s: ViewState) => boolean,
  write: (on: boolean) => Partial<ViewState>,
): HTMLLabelElement {
  const { row, input } = switchRow(label, store);
  input.checked = read(store.state);
  input.addEventListener("change", () => store.set(write(input.checked)));
  store.subscribe((s) => {
    input.checked = read(s);
  });
  return row;
}

export function sectionTitle(store: Store, text: (m: Messages) => string): HTMLHeadingElement {
  const h = document.createElement("h3");
  h.className = "section-title";
  localize(store, (m) => setText(h, text(m)));
  return h;
}
